import React, { useState, useEffect } from 'react';
import AppModal from '../../../components/organisms/CustomModal';
import PageTitle from 'components/atoms/PageTitle';
import FormInput from 'components/atoms/FormInput';
import Button from 'components/atoms/Button';
import Spinner from 'components/atoms/Spinner';
import { BASE_URL, postRequest, loggedUser_baseURL } from 'services/http';
import { connect, useSelector } from 'react-redux';
import Alert from 'components/atoms/Alert';
import arrowUp from '../../../assets/svg/arrow-up.svg';
import arrowDown from '../../../assets/svg/arrow-down.svg';
import closeIcon from '../../../assets/svg/close_x_red.svg';
import DropdownCard from 'components/atoms/Dropdown/DropdownCard';
import { toNumber } from 'lodash';
import { channel } from 'diagnostics_channel';

interface CommissionFee {
    channel: string;
    commissionPercentage: string;
    cappedAmount: string;
    internationalPaymentOptionFee: string;
}

const SetCommission = (props) => {
    const [loading, setLoading] = useState(false);
    const [commissionFee, setCommissionFee] = useState<CommissionFee[]>([]);
    const [paymentChannelDropdown, setPaymentChannelDropdown] = useState(false);
    const [selectedPaymentChannel, setSelectedPaymentChannel] = useState('');
    const [commissionPercent, setCommissionPercent] = useState('');
    const [paymentCapAmount, setPaymentCapAmount] = useState('');
    const [internationalPaymentOpt, setInternationalPaymentOpt] = useState('');

    const inputAlphaNumber = /^\d*\.?(?:\d{1,2})?$/;

    const businessID = props.businessId;
    const countries = useSelector((state: any) => state.app.countries);
    const businessCountry = countries?.find((c: { name: string; }) => c.name.toLowerCase() === props?.businessDetails?.businessCountry.toLowerCase());

    const paymentChannelList = businessCountry?.name === "NIGERIA"
        ? businessCountry?.defaultPaymentOptions.filter((channel: { name: string; }) => channel.name !== 'MOMO').map((op: any) => ({
            label: op?.code,
            value: op?.code
        })) || []

        : businessCountry?.defaultPaymentOptions.map((op: any) => ({
            label: op?.code,
            value: op?.code
        })) || []

    useEffect(() => {
        if (selectedPaymentChannel) {
            addNewCommission();
        }
    }, [selectedPaymentChannel]);

    const setCommissionFunc = async (reqBody: any, type: string) => {
        setLoading(true);
        const res = await postRequest(`${BASE_URL + loggedUser_baseURL}commission`, {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${props.bearerToken}`,
            "Public-Key": `${props.publicKey}`
        }, reqBody);

        if (res?.data.status !== "FAILED") {
            Alert('success', res?.data?.message);
            if (type === "submit") props.toggleModal();
            props.setRefresh(prevState => !prevState);
            setLoading(false);
        } else {
            Alert('error', res?.data?.message?.split(":")?.[1] || res?.data?.message);
            setLoading(false);
        }
    };

    const resetFieldValues = () => {
        setSelectedPaymentChannel('');
        setCommissionPercent('');
        setPaymentCapAmount('');
        setInternationalPaymentOpt('');
    };

    const addNewCommission = () => {
        if (selectedPaymentChannel) {
            // Check if the selected payment channel is already added
            const channelExists = commissionFee.some(fee => fee.channel === selectedPaymentChannel);
            if (channelExists) {
                Alert('error', 'This payment channel has already been added');
                return;
            }

            const newCommissionFee = {
                channel: selectedPaymentChannel,
                commissionPercentage: commissionPercent,
                cappedAmount: paymentCapAmount,
                internationalPaymentOptionFee: internationalPaymentOpt
            };

            setCommissionFee(prev => [...prev, newCommissionFee]);
            resetFieldValues();
        }
    };

    const handleSelectChannel = (item: any) => {
        setSelectedPaymentChannel(item.value);
        setPaymentChannelDropdown(false);
    };

    const handleRemoveCommission = (channel: any) => {
        setCommissionFee(commissionFee.filter(fee => fee.channel !== channel));
    };

    const handleSaveCommissionFee = async (e) => {
        e.preventDefault();
        if (commissionFee.length > 0) {
            await setCommissionFunc({
                channels: commissionFee,
                businessId: businessID,
            }, "submit");
        } else {
            Alert('error', 'Please fill all fields and add at least one commission fee');
        }
    };

    return (
        <AppModal
            handleClose={props.toggleModal}
            modalStyle="max-w-xl w-full mobile:w-4/5 max-h-[80vh] mobile:max-h-[90vh] overflow-y-hidden h-fit bg-white z-30 top-0 left-0 mx-auto z-30 right-0 p-5 rounded-xl animate-slide_up shadow-[0_8px_30px_rgb(0,0,0,0.12)] transition-all"
            contentStyle="flex flex-col gap-5 justify-between min-h-[40vh] h-[100%]"
        >
            <form onSubmit={handleSaveCommissionFee} className='flex flex-col justify-between gap-8 min-h-[40vh]'>
                <div className='flex flex-col gap-4'>
                    <div className='flex flex-col gap-5'>
                        <PageTitle
                            pageTitle='Set Fees'
                            subTitle='Set transacting fees for the business'
                            style='!mb-0'
                        />

                        <div className={`w-full relative z-[22]`}>
                            <div className='flex flex-col gap-2'>
                                <label className='font-semibold text-sm leading-6 text-Black2'>Payment Channel</label>
                                <div className='relative border border-transparent text-Black2 w-full bg-white rounded-md outline-none py-2 px-2 cursor-pointer' onClick={() => setPaymentChannelDropdown(true)}>
                                    <div className={`${commissionFee.length > 0 ? 'text-DarkBg3' : 'text-BackDrop_d_md'} flex justify-start items-center gap-3 max-w-[93%] overflow-x-scroll custom_container`}>
                                        {commissionFee.length > 0 ?
                                            commissionFee.map((item, i) => (
                                                <span key={i} className='group animate-fade_in px-3 py-1 border rounded-md flex justify-between items-center gap-2 cursor-pointer'>
                                                    {item.channel}
                                                    <img
                                                        src={closeIcon} alt='closeIcon'
                                                        onClick={() => handleRemoveCommission(item.channel)}
                                                        className='w-3 h-3 hidden group-hover:block group-hover:animate-fade_in transition ease-in duration-200'
                                                    />
                                                </span>
                                            )).reverse()
                                            : "Select Payment Channel"
                                        }
                                    </div>
                                    <div className='absolute right-3 top-0 bottom-0 w-4 h-4 my-auto cursor-pointer'>
                                        <img src={paymentChannelDropdown ? arrowUp : arrowDown} alt='dropdown_icon' className='w-full h-full' />
                                    </div>
                                </div>
                                <span className='text-Danger text-xs '>{ !props?.businessDetails?.businessCountry && 'This business is not properly setup, it is missing the business country.'}</span>
                            </div>
                            {paymentChannelDropdown && (
                                <DropdownCard
                                    handleClickOut={() => setPaymentChannelDropdown(false)}
                                    cardLayout={`min-w-[150px] w-3/4 max-h-4/5 overflow-scroll flex flex-col gap-0 absolute z-40 right-5 top-8 p-1 bg-white rounded-md shadow-[0_8px_30px_rgb(0,0,0,0.12)] h-fit animate-slide_down2`}
                                >
                                    {paymentChannelList.length === 0 ? (
                                        <span className='cursor-default font-normal text-sm leading-6 text-Primary rounded-md p-2 hover:bg-BackDrop_d_xs'>
                                            Not found
                                        </span>
                                    ) : (
                                        paymentChannelList.map((item, i) => (
                                            <span key={i} onClick={() => handleSelectChannel(item)} className='cursor-pointer font-normal text-sm leading-6 text-Black2 rounded-md p-2 hover:bg-BackDrop_d_xs'>
                                                {item.label.length > 25 ? item.label.slice(0, 25) + "..." : item.label}
                                            </span>
                                        ))
                                    )}
                                </DropdownCard>
                            )}
                        </div>
                    </div>

                    <div className='flex flex-col gap-5 mobile:gap-8 max-h-[40vh] overflow-y-scroll'>
                        {commissionFee.map((item, index) => (
                            <div key={index} id='form_content' className='flex flex-col gap-2'>
                                <div className='flex justify-between items-center gap-5'>
                                    <p className='font-semibold text-sm leading-6 text-SecondaryAccent'>Payment Channel: {item.channel}</p>
                                    <img
                                        src={closeIcon} alt='closeIcon'
                                        onClick={() => handleRemoveCommission(item.channel)}
                                        className='w-3 h-3 block group-hover:animate-fade_in transition ease-in duration-200 cursor-pointer'
                                    />
                                </div>
                                <div className='flex mobile:flex-col gap-3'>
                                    <FormInput
                                        type='text'
                                        name={`commission_percent_${index}`}
                                        placeholder='Local Percentage'
                                        style='w-full'
                                        inputStyle='border border-transparent text-Black2 w-full'
                                        value={item.commissionPercentage}
                                        onChange={e => {
                                            const updatedCommissionFee = [...commissionFee];
                                            const value = e.target.value;
                                            if (inputAlphaNumber.test(value)) {
                                                updatedCommissionFee[index].commissionPercentage = value;
                                                if (toNumber(value) > 100) {
                                                    updatedCommissionFee[index].commissionPercentage = '100';
                                                } else {
                                                    updatedCommissionFee[index].commissionPercentage = value;
                                                }
                                                setCommissionFee(updatedCommissionFee);
                                            }
                                        }}
                                    />
                                    <FormInput
                                        type='text'
                                        name={`international_payment_opt_${index}`}
                                        placeholder='International Payment Option Fee'
                                        style='w-full'
                                        inputStyle='border border-transparent text-Black2 w-full'
                                        value={item.internationalPaymentOptionFee}
                                        onChange={e => {
                                            const updatedCommissionFee = [...commissionFee];
                                            const value = e.target.value;
                                            if (inputAlphaNumber.test(value)) {
                                                updatedCommissionFee[index].internationalPaymentOptionFee = value;
                                                setCommissionFee(updatedCommissionFee);
                                            }
                                        }}
                                    />
                                    <FormInput
                                        type='text'
                                        name={`payment_cap_amount_${index}`}
                                        placeholder='Payment Cap Amount'
                                        style='w-full'
                                        inputStyle='border border-transparent text-Black2 w-full'
                                        value={item.cappedAmount}
                                        onChange={e => {
                                            const updatedCommissionFee = [...commissionFee];
                                            const value = e.target.value;
                                            if (inputAlphaNumber.test(value)) {
                                                updatedCommissionFee[index].cappedAmount = value;
                                                setCommissionFee(updatedCommissionFee);
                                            }
                                        }}
                                    />
                                </div>
                            </div>
                        )).reverse()}
                    </div>
                </div>

                <div className='flex justify-end gap-3 items-center'>
                    <Button
                        btnType='submit'
                        btnText={loading ? <Spinner text='Processing...' textStyle='text-white' /> : "Save & Submit"}
                        btnStyle={`bg-DarkBg3 text-white font-normal text-[15px] leading-5 p-3 w-full cursor-pointer`}
                        onClick={() => {}}
                    />
                </div>
            </form>
        </AppModal>
    );
};

const mapStateToProps = (state: any) => ({
    userData: state.auth.user_authData,
    bearerToken: state.auth.user_authData.token.token,
    publicKey: state.auth.user_authData?.live_public_key
});

export default connect(mapStateToProps)(SetCommission);